import { type CacheBackend } from "@pdfking/compatibility"
import {
  CachedForward,
  type MessageRouter,
  type ReceiverInput,
} from "@pdfking/functionpool"
import {
  CreateSingleton,
  Peek,
  SafeIterable,
  type StringGUID,
} from "@pdfking/tfunc"

import type { APIRequest } from "../types/api/APIRequest"
import type { APIResponse } from "../types/api/APIResponse"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { TaskOptions } from "../types/tasks/TaskOptions"
import type { TrackingInterface } from "../types/tasks/tracking/TrackingInterface"

import { AutoThrow } from "../errors/AutoThrow"
import { TaskName } from "../types/tasks/TaskName"
import { CreateEngineVirtual } from "./CreateEngineVirtual"

const emit_callbacks = (
  forward: MessageRouter<APIRequest, APIResponse>,
  tracking_callback: TrackingInterface | undefined = undefined
): MessageRouter<APIRequest, APIResponse> =>
  async function* (original) {
    const { guid, input } = await extract_task_id(original)

    tracking_callback?.start(guid)

    const { error, stream } = SafeIterable(forward(input))

    for await (const response of stream) {
      if (response.enum == TaskName.TRACKING) {
        tracking_callback?.push(guid, response)
      } else {
        yield response
      }
    }

    tracking_callback?.stop(guid, await error)

    await AutoThrow(error)
  }

const extract_task_id = (
  input: ReceiverInput<APIRequest>
): Promise<{
  cache_evict: boolean
  guid: StringGUID
  input: ReceiverInput<APIRequest>
}> =>
  Peek(input).then(({ stream, value }) => ({
    cache_evict: (value as TaskOptions).cache_evict,
    guid: (value as TaskOptions).task_id,
    input: stream,
  }))

export const CreateEngineMaster = (
  config: Partial<PdfEngineOptions>,
  factory: (
    router: MessageRouter<APIRequest, APIResponse>
  ) => MessageRouter<APIRequest, APIResponse>,
  cache: CacheBackend<APIResponse[]> | undefined = undefined,
  tracking_callback: TrackingInterface | undefined = undefined
): PdfEngine => {
  const router = CreateSingleton(() => factory(forward))

  const forward: MessageRouter<APIRequest, APIResponse> = CachedForward(
    emit_callbacks(t => router()(t), tracking_callback),
    extract_task_id,
    cache
  )

  return CreateEngineVirtual(config, forward)
}
